import { io, type Socket } from 'socket.io-client';

import { ConnectionState, type RetryConfig } from '../types.js';

export interface SocketConnectionOptions {
  url: string;
  socketOptions: Record<string, unknown>;
  onConnect?: () => void | Promise<void>;
  onDisconnect?: (reason: string) => void;
  onReconnect?: (attemptNumber: number) => void | Promise<void>;
  onError?: (error: Error) => void;
  logger?: {
    log: (message: string, ...args: unknown[]) => void;
    error: (message: string, ...args: unknown[]) => void;
    warn: (message: string, ...args: unknown[]) => void;
  };
}

export class SocketConnectionManager {
  private socket: Socket | null = null;
  private connectionState: ConnectionState = ConnectionState.DISCONNECTED;
  private retryAttempt: number = 0;
  private retryTimeout: NodeJS.Timeout | null = null;
  private hasConnectedOnce: boolean = false;

  private readonly retryConfig: RetryConfig = {
    maxInitialAttempts: 10,
    initialDelay: 1_000,
    maxDelay: 15_000,
    backoffMultiplier: 2,
  };

  private readonly CONNECTION_TIMEOUT = 2_000;
  private readonly serviceName: string;
  private readonly options: SocketConnectionOptions;
  private readonly logger: NonNullable<SocketConnectionOptions['logger']>;

  constructor(
    serviceName: string,
    options: SocketConnectionOptions,
    retryConfig?: Partial<RetryConfig>,
  ) {
    this.serviceName = serviceName;
    this.options = options;
    this.logger = options.logger || console;

    if (retryConfig) {
      this.retryConfig = { ...this.retryConfig, ...retryConfig };
    }
  }

  public async connect(): Promise<void> {
    if (this.connectionState === ConnectionState.CONNECTED) {
      this.logger.log(`[${this.serviceName}] Already connected`);
      return;
    }

    if (
      this.connectionState === ConnectionState.CONNECTING ||
      this.connectionState === ConnectionState.RETRYING
    ) {
      this.logger.log(
        `[${this.serviceName}] Connection attempt already in progress`,
      );

      return;
    }

    this.logger.log(
      `[${this.serviceName}] Starting connection process to Socket.IO bridge at:`,
      this.options.url,
    );

    // Start connection attempt without blocking.
    this.startConnectionAttempt();
  }

  private async startConnectionAttempt() {
    this.retryAttempt = 0;

    try {
      await this.connectWithRetry();
    } catch (error) {
      this.logger.error(
        `[${this.serviceName}] Initial connection attempts failed:`,
        error,
      );

      // If we've never connected successfully, we've exhausted our retry attempts
      // The user will need to manually retry or fix the issue
      this.connectionState = ConnectionState.FAILED;
    }
  }

  private async connectWithRetry(): Promise<void> {
    let delay = this.retryConfig.initialDelay;

    while (this.retryAttempt < this.retryConfig.maxInitialAttempts) {
      try {
        this.connectionState =
          this.retryAttempt === 0
            ? ConnectionState.CONNECTING
            : ConnectionState.RETRYING;

        this.logger.log(
          `[${this.serviceName}] Connection attempt ${this.retryAttempt + 1}/${this.retryConfig.maxInitialAttempts}`,
        );

        await this.connectSocket();

        this.connectionState = ConnectionState.CONNECTED;
        this.retryAttempt = 0;

        this.logger.log(
          `[${this.serviceName}] Successfully connected to Socket.IO bridge`,
        );

        this.clearRetryTimeouts();

        if (this.options.onConnect) {
          await this.options.onConnect();
        }

        return;
      } catch (error) {
        this.retryAttempt++;

        this.logger.error(
          `[${this.serviceName}] Connection attempt ${this.retryAttempt} failed:`,
          error,
        );

        if (this.socket) {
          this.socket.disconnect();
          this.socket = null;
        }

        if (this.retryAttempt >= this.retryConfig.maxInitialAttempts) {
          this.connectionState = ConnectionState.FAILED;

          throw new Error(
            `Failed to connect after ${this.retryConfig.maxInitialAttempts} attempts`,
          );
        }

        this.logger.log(
          `[${this.serviceName}] Waiting ${delay}ms before retry...`,
        );

        await this.delay(delay);

        delay = Math.min(
          delay * this.retryConfig.backoffMultiplier,
          this.retryConfig.maxDelay,
        );
      }
    }
  }

  private async connectSocket(): Promise<void> {
    return new Promise((resolve, reject) => {
      this.logger.log(
        `[${this.serviceName}] Creating Socket.IO connection (${this.options.url})...`,
      );

      this.socket = io(this.options.url, this.options.socketOptions);

      // Log the actual socket options being used.
      this.logger.log(`[${this.serviceName}] Socket.IO options:`, {
        reconnection: this.socket.io.opts.reconnection,
        reconnectionAttempts: this.socket.io.opts.reconnectionAttempts,
        reconnectionDelay: this.socket.io.opts.reconnectionDelay,
        reconnectionDelayMax: this.socket.io.opts.reconnectionDelayMax,
      });

      const connectionTimeout = setTimeout(() => {
        this.logger.error(`[${this.serviceName}] Connection timeout`);

        if (this.connectionState !== ConnectionState.CONNECTED) {
          this.socket?.disconnect();
          reject(new Error('Connection timeout'));
        }
      }, this.CONNECTION_TIMEOUT);

      this.socket.on('connect', async () => {
        clearTimeout(connectionTimeout);

        const isReconnection = this.hasConnectedOnce;

        // Socket ID might not be immediately available, wait a tick
        setTimeout(() => {
          this.logger.log(`[${this.serviceName}] Socket connected:`, {
            socketId: this.socket?.id,
            firstConnection: !isReconnection,
            isReconnection,
          });
        }, 0);

        // If this is a reconnection (not the first connect), treat it as a reconnect
        // This handles server restarts where 'reconnect' event might not fire
        if (isReconnection) {
          this.logger.log(
            `[${this.serviceName}] Treating connect as reconnection (server may have restarted)`,
          );
          this.connectionState = ConnectionState.CONNECTED;

          if (this.options.onReconnect) {
            // Call onReconnect to re-register instance
            await this.options.onReconnect(0);
          }
        }

        this.hasConnectedOnce = true;
        resolve();
      });

      this.socket.on('disconnect', (reason: string) => {
        this.logger.log(`[${this.serviceName}] Socket disconnected:`, {
          reason,
          socketId: this.socket?.id,
        });

        this.connectionState = ConnectionState.DISCONNECTED;

        if (this.options.onDisconnect) {
          this.options.onDisconnect(reason);
        }

        // Don't attempt to reconnect if we're manually disconnecting.
        const isManualDisconnect = reason === 'io client disconnect';

        if (!isManualDisconnect && this.hasConnectedOnce) {
          // After successful initial connection, rely entirely on Socket.IO's reconnection
          this.logger.log(
            `[${this.serviceName}] Socket.IO will handle reconnection (reason: ${reason})`,
          );
        }
      });

      // Listen for reconnection attempts.
      this.socket.on('reconnect_attempt', (attemptNumber: number) => {
        this.logger.log(`[${this.serviceName}] Socket.IO reconnect attempt:`, {
          attemptNumber,
        });
      });

      this.socket.on('reconnect', (attemptNumber: number) => {
        this.logger.log(`[${this.serviceName}] Socket reconnected:`, {
          attemptNumber,
          socketId: this.socket?.id,
        });

        this.connectionState = ConnectionState.CONNECTED;

        if (this.options.onReconnect) {
          this.options.onReconnect(attemptNumber);
        }
      });

      this.socket.on('reconnect_error', (error: Error) => {
        this.logger.error(
          `[${this.serviceName}] Socket.IO reconnect error:`,
          error,
        );
      });

      this.socket.on('reconnect_failed', () => {
        this.logger.error(
          `[${this.serviceName}] Socket.IO reconnection failed after all attempts`,
        );

        this.connectionState = ConnectionState.FAILED;

        // Socket.IO has exhausted its reconnection attempts
        // The connection is now permanently failed until manual intervention
      });

      this.socket.on('error', (error) => {
        this.logger.error(`[${this.serviceName}] Socket error:`, error);

        if (this.connectionState !== ConnectionState.CONNECTED) {
          clearTimeout(connectionTimeout);
          reject(error);
        }

        if (this.options.onError) {
          this.options.onError(error);
        }
      });

      this.socket.on('auth_error', (error) => {
        this.logger.error(`[${this.serviceName}] Authentication error:`, error);
        clearTimeout(connectionTimeout);
        reject(new Error(error.message || 'Authentication failed'));
      });
    });
  }

  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => {
      this.retryTimeout = setTimeout(resolve, ms);
    });
  }

  // 1. Custom retry for initial connection attempts.
  // 2. Socket.IO's built-in reconnection after successful initial connection.

  private clearRetryTimeouts() {
    if (this.retryTimeout) {
      clearTimeout(this.retryTimeout);
      this.retryTimeout = null;
    }
  }

  public async disconnect(): Promise<void> {
    this.logger.log(`[${this.serviceName}] Disconnecting...`);

    this.clearRetryTimeouts();

    if (this.socket) {
      this.socket.removeAllListeners();
      this.socket.disconnect();
      this.socket = null;
    }

    this.connectionState = ConnectionState.DISCONNECTED;

    this.logger.log(`[${this.serviceName}] Disconnected`);
  }

  public getSocket(): Socket | null {
    return this.socket;
  }

  public getConnectionState(): ConnectionState {
    return this.connectionState;
  }

  public isConnected(): boolean {
    return (
      this.connectionState === ConnectionState.CONNECTED &&
      this.socket?.connected === true
    );
  }

  public async reconnect(): Promise<void> {
    if (this.connectionState === ConnectionState.CONNECTED) {
      this.logger.log(`[${this.serviceName}] Already connected`);
      return;
    }

    this.logger.log(`[${this.serviceName}] Manual reconnection requested`);

    this.hasConnectedOnce = false;

    await this.disconnect();
    await this.connect();
  }
}
